import {
  faBook,
  faBoxArchive,
  faCircleQuestion,
  faGraduationCap,
  faImages,
  faLightbulb,
  faNewspaper,
  faShoppingBag,
  faVideoCamera,
  faVolumeHigh,
} from '@fortawesome/free-solid-svg-icons';
import Pluralize from 'typescript-pluralize';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppContext } from 'provider/AppProvider/AppProvider';
import { useDefaultColorValue } from 'services/queries/app';
import { FC } from 'react';

import InnerHTML from 'dangerously-set-html-content';
import CheckboxIcon from 'assets/images/checkbox.png';
import defaultImage from 'assets/images/no-picture.png';
import { StyledHeader } from '../Hero/Hero.styles';
import { ICard } from './Card.interface';
import { StyledDescription } from './Card.styles';

export const CardIcon: FC<{ type: string }> = ({ type }) => {
  switch (type) {
    case 'videos':
    case 'video':
      return <FontAwesomeIcon icon={faVideoCamera} size="2xl" />;
    case 'articles':
    case 'article':
      return <FontAwesomeIcon icon={faNewspaper} size="2xl" />;
    case 'resources':
    case 'resource':
      return <FontAwesomeIcon icon={faBoxArchive} size="2xl" />;
    case 'questions':
    case 'question':
      return <FontAwesomeIcon icon={faCircleQuestion} size="2xl" />;
    case 'tips':
    case 'tip':
      return <FontAwesomeIcon icon={faLightbulb} size="2xl" />;
    case 'lessons':
    case 'lesson':
      return <FontAwesomeIcon icon={faGraduationCap} size="2xl" />;
    case 'journals':
    case 'journal':
      return <FontAwesomeIcon icon={faBook} size="2xl" />;
    case 'products':
    case 'product':
      return <FontAwesomeIcon icon={faShoppingBag} size="2xl" />;
    case 'audios':
    case 'audio':
      return <FontAwesomeIcon icon={faVolumeHigh} size="2xl" />;
    case 'galleries':
    case 'gallery':
      return <FontAwesomeIcon icon={faImages} size="2xl" />;
    case 'action_lists':
    case 'action_list':
      return (
        <img src={CheckboxIcon} width="28" height="28" alt="ActionList Icon" />
      );
    default:
      return null;
  }
};

const Card: FC<ICard> = ({
  type,
  header,
  title,
  author,
  description,
  image,
  alt,
  id,
}) => {
  const { light } = useDefaultColorValue();
  const {
    username,
    setup: { fonts },
    baseURL,
    fallBackImage,
  } = useAppContext();

  const content_type = type === 'action_list' ? 'action-list' : type;

  return (
    <div className="mx-auto container">
      <div
        className="flex flex-col justify-between p-4 m-2 rounded-md shadow-lg shadow-white-500/40 h-44"
        style={{ background: light }}
      >
        <span className="flex flex-row items-center justify-start mb-2 2xl:mb-4">
          <CardIcon type={content_type} />
          <StyledHeader
            font={fonts?.header}
            className="text-gray-900 text-xl 2xl:text-2xl leading-tight font-bold pl-4 mb-0 uppercase"
          >
            {`FEATURED ${header === 'action_list' ? 'List' : header}`}
          </StyledHeader>
        </span>

        <div className="flex max-h-32 h-full">
          <div className="flex justify-center items-center">
            <img
              className="object-cover rounded-md w-20 h-20 xl:w-24 xl:h-24"
              src={image === '' ? fallBackImage || defaultImage : image}
              alt={alt}
            />
          </div>
          <div className="float-right flex flex-col justify-start max-w-[calc(100%-80px)] xl:max-w-[calc(100%-96px)] pl-4">
            <div className="pb-3">
              <a
                href={
                  (username ?? '') !== '' &&
                  (content_type ?? '') !== '' &&
                  (id ?? '') !== 'id'
                    ? `${String(baseURL)}/${String(
                        Pluralize.plural(content_type),
                      )}/${String(id)}`
                    : '/'
                }
                className="underline text-blue-900 text-lg font-bold line-clamp-1 hover:no-underline"
              >
                {title}
              </a>
            </div>

            {typeof author !== 'undefined' && author?.length > 0 ? (
              <p className="text-gray-400 text-sm font-medium">{`By ${author}`}</p>
            ) : null}
            <StyledDescription
              className="text-gray-700 text-base mb-0 whitespace-pre-wrap w-full inner-html"
              line={typeof author !== 'undefined' && author?.length > 0 ? 1 : 2}
            >
              <InnerHTML
                html={
                  description === null ||
                  description === '' ||
                  typeof description === 'undefined'
                    ? '<p></p>'
                    : description
                }
                className="inner-html text-gray-700 text-base mb-0 w-full whitespace-pre-wrap ttci-break-word no-overflow"
              />
            </StyledDescription>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Card;
